/**
 * The stage: who the assistant is (an avatar) and where it stands (a background), two choices apart. The build's
 * catalog of both is data (src/app/stage.json, assets in public/static/avatars and public/static/backgrounds, made by
 * tools/make-stage-assets.sh); a pack says which it wants (DomainPack.stage), Settings may choose another background.
 *
 * An avatar is filmed on a green screen and the browser keys the green out (avatar/KeyedVideo.tsx), so it stands on
 * any background. The live MuseTalk frames are not keyed yet: they carry the background of their plate, until the
 * MuseTalk avatar is made again from the green-screen clip.
 */
import { z } from 'zod';

/** A name in each language the catalog writes. */
const label = z.record(z.string(), z.string());

const avatarSchema = z.object({
  label,
  /** The idle loop (the clip then the same reversed) and its first frame, both on the green screen. */
  idle: z.object({ video: z.string(), poster: z.string() }).strict(),
  /** Where the face is in the frame (fractions of it), and how tall the frame is per circle diameter: the crops. */
  face: z.object({ cx: z.number(), cy: z.number(), frameHeightPerDiameter: z.number().positive() }).strict(),
  /** The screen's colour and how it is keyed out: chroma distances, and how much green is taken off the edges (0-1). */
  key: z.object({
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    similarity: z.number().min(0),
    smoothness: z.number().min(0),
    spill: z.number().min(0).max(1),
  }).strict(),
  /** The plate whose recorded mouths make the idle face speak (avatar_plate/<id>/visemes, docs/NEW-UI/09). */
  mouths: z.string().regex(/^[A-Za-z0-9_-]+$/).optional(),
}).strict();

const backgroundSchema = z.object({ label, image: z.string() }).strict();

export const stageCatalogSchema = z.object({
  avatars: z.record(z.string(), avatarSchema),
  backgrounds: z.record(z.string(), backgroundSchema),
}).strict();

export type StageCatalog = z.infer<typeof stageCatalogSchema>;
export type Avatar = z.infer<typeof avatarSchema>;
export type Background = z.infer<typeof backgroundSchema>;
export type ChromaKey = Avatar['key'];

export const EMPTY_STAGE: StageCatalog = { avatars: {}, backgrounds: {} };

/** The background chosen in Settings: the pack's (`auto`), none (the theme's plain one), or one of the catalog. */
export const BACKGROUND_AUTO = 'auto';
export const BACKGROUND_NONE = 'none';

/** What a pack asks of the stage, by catalog id. */
export interface StageWish {
  background?: string;
  avatar?: string;
}

export interface Stage {
  background: Background | null;
  avatar: Avatar | null;
}

/**
 * The stage shown: the background chosen in Settings, else the pack's, else none; the pack's avatar, else the
 * catalog's first. A choice the catalog no longer has (saved by an older build) is the pack's, not an empty room.
 */
export function stageOf(catalog: StageCatalog, wish: StageWish | undefined, chosenBackground: string): Stage {
  const chosen = chosenBackground === BACKGROUND_NONE || chosenBackground in catalog.backgrounds ? chosenBackground : BACKGROUND_AUTO;
  const backgroundId = chosen === BACKGROUND_AUTO ? wish?.background : chosen === BACKGROUND_NONE ? undefined : chosen;
  const avatarId = wish?.avatar ?? Object.keys(catalog.avatars)[0];
  return {
    background: (backgroundId && catalog.backgrounds[backgroundId]) || null,
    avatar: (avatarId && catalog.avatars[avatarId]) || null,
  };
}

/** A catalog name in the screen's language, else Italian, else the id. */
export function labelOf(entry: { label: Record<string, string> }, language: string, id: string): string {
  return entry.label[language] ?? entry.label.it ?? id;
}

/** The CSS background of a stage's backdrop: its image over the scene's own colour. */
export function backdrop(background: Background | null, fallback: string): string {
  return background ? `url("${background.image}") center / cover no-repeat, ${fallback}` : fallback;
}
